import React, { useEffect, useState } from "react";
import { api, upload } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { stamp } from "./Pass";
import { PersonBanner, type PersonHeader } from "./Otc";
import { PersonButtons } from "./Visits";
import { StaffImport, StaffManualAdd } from "./StaffImport";

/**
 * The Staff section (build plan §10.1 #7; D2 #3 + addendum): the staff
 * medical records, imported from the staff application export or added by
 * hand. Each row has Chart, Give an OTC item and Start a visit; the record
 * shows the medical subset, its documents and every edit made in the app.
 * Staff records are read by the same roles as student records.
 */

export interface StaffView {
  staffKey: string;
  sourceForm: string;
  sourceEntryId: string;
  manual: boolean;
  fields: Record<string, string>;
  sourceEntryUpdated: string;
  importedAtUtc: string;
  importedByEmail: string;
  active: boolean;
}
interface Can {
  edit: boolean;
  import: boolean;
  visit: boolean;
  otc: boolean;
}
interface DocView {
  docKey: string;
  fileName: string;
  uploadedByEmail: string;
  uploadedAtUtc: string;
  notes: string;
}
interface Edit {
  noteKey: string;
  atUtc: string;
  byEmail: string;
  byName: string;
  changes: Record<string, { old: string; new: string }>;
}

export const headerOf = (s: StaffView): PersonHeader => ({ personKey: s.staffKey, personType: "Staff", lastName: s.fields.LastName, firstName: s.fields.FirstName, dateOfBirth: s.fields.DateOfBirth, city: "", departed: !s.active, staffAllergies: s.fields.AllergiesText, allergyBanner: null });
const sourceLine = (s: StaffView) => (s.manual ? "Added by hand" : `Imported from ${s.sourceForm}${s.importedAtUtc ? ` on ${stamp(s.importedAtUtc)}` : ""}`);

function StaffList() {
  const [data, setData] = useState<{ staff: StaffView[]; can: Can } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const load = () => void api<{ staff: StaffView[]; can: Can }>("GET", "/api/staff").then((r) => (r.ok ? setData(r.data) : setError(r.data.error ?? "Could not load.")));
  useEffect(load, []);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!data) return <p className="text-sm text-slate-600">Loading…</p>;
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = data.staff.filter((s) => words.every((w) => `${s.fields.FirstName} ${s.fields.LastName} ${s.staffKey} ${s.fields.Role}`.toLowerCase().includes(w)));
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Staff</h2>
      <p className="mb-3 text-sm text-slate-600">Staff and volunteers: sick-call visits and incidents, and OTC items they take themselves. No MAR, no parent permissions.</p>
      {data.can.import && <StaffImport onDone={load} />}
      {data.can.import && <StaffManualAdd onDone={load} />}
      <div className="section-card">
        <label htmlFor="sf-q" className="field-label">Find</label>
        <input id="sf-q" className="field-input w-full" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, role or STAFF- number" autoComplete="off" />
        {shown.length === 0 ? <p className="mt-2 text-sm text-slate-600">{data.staff.length ? "No one matches." : "No staff records yet."}</p> : (
          <ul className="mt-2 divide-y divide-slate-100">
            {shown.map((s) => (
              <li key={s.staffKey} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span className="min-w-0">
                  <a className="font-medium text-program underline" href={`/staff/${encodeURIComponent(s.staffKey)}`}>{s.fields.LastName}, {s.fields.FirstName}</a>{" "}
                  <span className="rounded bg-sky-100 px-1.5 text-xs font-semibold text-sky-900">staff</span>
                  {!s.active && <span className="ml-1 rounded bg-slate-200 px-1.5 text-xs">inactive</span>}
                  <span className="block text-slate-600">{s.staffKey} · {s.fields.Role || "—"} · {sourceLine(s)}</span>
                </span>
                <PersonButtons p={headerOf(s)} canVisit={data.can.visit} canOtc={data.can.otc} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const ADDRESS_PARTS = [["line1", "Address line 1"], ["line2", "Address line 2"], ["city", "City"], ["state", "State"], ["zip", "ZIP"]] as const;
const addressOf = (json: string): Record<string, string> => {
  try {
    const v = JSON.parse(json || "{}");
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
};
const addressLine = (json: string) => {
  const a = addressOf(json);
  return [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(", "), a.zip].filter(Boolean).join(" · ") || "—";
};

/** The fields the edit form shows, in order (the JSON groups the import fills are shown, not edited here). */
const EDIT_FIELDS: [string, string, "text" | "area" | "date" | "email" | "tel" | "role" | "yn"][] = [
  ["FirstName", "First name", "text"], ["MiddleName", "Middle name", "text"], ["LastName", "Last name", "text"], ["Suffix", "Suffix", "text"],
  ["Role", "Role", "role"], ["DateOfBirth", "Date of birth", "date"], ["Email", "Email", "email"], ["Phone", "Phone", "tel"],
  ["EmergencyContactName", "Emergency contact", "text"], ["EmergencyContactPhone", "Emergency contact phone", "tel"], ["EmergencyContactEmail", "Emergency contact email", "email"],
  ["Insurer", "Insurer", "text"], ["InsurancePolicyNumber", "Policy number", "text"], ["InsurancePolicyHolder", "Policy holder", "text"], ["InsuranceGroupNumber", "Group number", "text"],
  ["ProviderName", "Provider", "text"], ["ProviderPhone", "Provider phone", "tel"],
  ["MedicationsText", "Medications", "area"], ["AllergiesText", "Allergies", "area"], ["PresentIllnesses", "Present illnesses", "area"], ["PastIllnesses", "Past illnesses", "area"],
  ["PhysicalLimitations", "Physical limitations", "yn"], ["PhysicalLimitationsDetail", "Physical limitations (detail)", "area"],
];

function StaffEditForm({ s, roles, onSaved, onCancel }: { s: StaffView; roles: string[]; onSaved: (s: StaffView, edits: Edit[]) => void; onCancel: () => void }) {
  const [f, setF] = useState<Record<string, string>>({ ...s.fields });
  const [addr, setAddr] = useState<Record<string, string>>(addressOf(s.fields.AddressJson));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { problems, check } = useProblems([
    ["se-FirstName", !f.FirstName?.trim(), "A staff member needs a first name."],
    ["se-LastName", !f.LastName?.trim(), "A staff member needs a last name."],
  ]);
  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const a = Object.fromEntries(Object.entries(addr).filter(([, v]) => v.trim()));
    const r = await api<{ staff: StaffView; edits: Edit[] }>("PUT", `/api/staff/${encodeURIComponent(s.staffKey)}`, { ...Object.fromEntries(EDIT_FIELDS.map(([k]) => [k, f[k] ?? ""])), Active: f.Active === "N" ? "N" : "Y", AddressJson: Object.keys(a).length ? JSON.stringify(a) : "" });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not saved.");
    onSaved(r.data.staff, r.data.edits);
  }
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {EDIT_FIELDS.map(([k, label, kind]) => (
          <div key={k} className={kind === "area" ? "sm:col-span-2" : ""}>
            <label htmlFor={`se-${k}`} className="field-label">{label}</label>
            {kind === "area" ? (
              <textarea id={`se-${k}`} className="field-input min-h-16" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
            ) : kind === "role" ? (
              <select id={`se-${k}`} className="field-input min-h-11" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}>{roles.map((r) => <option key={r} value={r}>{r}</option>)}</select>
            ) : kind === "yn" ? (
              <select id={`se-${k}`} className="field-input min-h-11" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}><option value="">—</option><option value="Y">Yes</option><option value="N">No</option></select>
            ) : (
              <input id={`se-${k}`} type={kind} className="field-input" {...invalid(`se-${k}`, problems)} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
            )}
            <FieldProblem id={`se-${k}`} problems={problems} />
          </div>
        ))}
        {ADDRESS_PARTS.map(([k, label]) => (
          <div key={k}><label htmlFor={`se-addr-${k}`} className="field-label">{label}</label><input id={`se-addr-${k}`} className="field-input" value={addr[k] ?? ""} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} /></div>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.Active !== "N"} onChange={(e) => setF({ ...f, Active: e.target.checked ? "Y" : "N" })} />Active (shown in the look-up)</label>
      <p className="text-xs text-slate-500">Each change is kept: who changed what, from what. A later import asks before it overwrites anything changed here.</p>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void save()}>Save the staff record</button>
        <button type="button" className="btn-secondary min-h-11" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function StaffDocuments({ staffKey, docs, canAdd, onDocs }: { staffKey: string; docs: DocView[]; canAdd: boolean; onDocs: (d: DocView[]) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const { problems, check, reset } = useProblems([
    ["sd-file", !file, "Choose a photo or a PDF."],
    ["sd-label", !label.trim(), "Say what the document is (e.g. insurance card, front)."],
  ]);
  async function send() {
    setError(null);
    setFlash(null);
    if (!check() || !file) return;
    setBusy(true);
    const r = await upload<{ documents: DocView[] }>(`/api/staff/${encodeURIComponent(staffKey)}/documents?name=${encodeURIComponent(file.name)}&label=${encodeURIComponent(label)}`, file);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not uploaded.");
    setFile(null);
    setLabel("");
    reset();
    setFlash("Document added");
    onDocs(r.data.documents);
  }
  return (
    <section className="section-card">
      <h3 className="mb-2 text-base font-semibold">Documents ({docs.length})</h3>
      {docs.length === 0 ? <p className="text-sm text-slate-600">None attached. Uploads don't come through the export: download them from the staff application and attach them here if the RN wants them on hand.</p> : (
        <ul className="space-y-1 text-sm">
          {docs.map((d) => <li key={d.docKey}><a className="text-program underline" href={`/api/med-documents/${encodeURIComponent(d.docKey)}`} target="_blank" rel="noreferrer">{d.notes || d.fileName}</a> <span className="text-xs text-slate-500">· {stamp(d.uploadedAtUtc)} · {d.uploadedByEmail}</span></li>)}
        </ul>
      )}
      {canAdd && (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div><label htmlFor="sd-file" className="field-label">Photo or PDF</label><input id="sd-file" type="file" accept="image/jpeg,image/png,application/pdf" className="field-input" {...invalid("sd-file", problems)} onChange={(e) => setFile(e.target.files?.[0] ?? null)} /><FieldProblem id="sd-file" problems={problems} /></div>
          <div><label htmlFor="sd-label" className="field-label">What it is</label><input id="sd-label" className="field-input" {...invalid("sd-label", problems)} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Insurance card (front)" /><FieldProblem id="sd-label" problems={problems} /></div>
          <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => void send()}>Upload the document</button>
        </div>
      )}
      {error && <p className="field-error" role="alert">{error}</p>}
      {flash && <p className="mt-1 text-sm font-medium text-emerald-800" role="status">{flash}</p>}
    </section>
  );
}

function StaffRecord({ staffKey }: { staffKey: string }) {
  const [d, setD] = useState<{ staff: StaffView; documents: DocView[]; edits: Edit[]; can: Can; labels: Record<string, string>; roles: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  useEffect(() => {
    void api<NonNullable<typeof d>>("GET", `/api/staff/${encodeURIComponent(staffKey)}`).then((r) => (r.ok ? setD(r.data) : setError(r.data.error ?? "Could not load.")));
  }, [staffKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  const s = d.staff;
  const F = s.fields;
  const row = (label: string, value: string) => (
    <div className="flex flex-wrap justify-between gap-x-3 border-b border-slate-100 py-1 text-sm"><span className="text-slate-600">{label}</span><span className="min-w-0 whitespace-pre-wrap break-words text-right">{value || "—"}</span></div>
  );
  return (
    <div>
      <p className="mb-3 text-sm"><a href="/staff" className="text-program underline">← Staff</a></p>
      <PersonBanner p={headerOf(s)} />
      <section className="section-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">{F.LastName}, {F.FirstName}{F.Suffix ? ` ${F.Suffix}` : ""}</h2>
          <PersonButtons p={headerOf(s)} canVisit={d.can.visit} canOtc={d.can.otc} />
        </div>
        <p className="mt-1 text-sm text-slate-600">{s.staffKey} · {F.Role || "—"} · {sourceLine(s)}{s.active ? "" : " · inactive"}</p>
        {flash && <p className="mt-1 text-sm font-medium text-emerald-800" role="status">{flash}</p>}
        {!editing ? (
          <>
            <div className="mt-3 grid gap-x-8 md:grid-cols-2">
              <div>
                {row("Date of birth", F.DateOfBirth)}
                {row("Email", F.Email)}
                {row("Phone", F.Phone)}
                {row("Address", addressLine(F.AddressJson))}
                {row("Emergency contact", [F.EmergencyContactName, F.EmergencyContactPhone, F.EmergencyContactEmail].filter(Boolean).join(" · "))}
                {row("Provider", [F.ProviderName, F.ProviderPhone, addressLine(F.ProviderAddressJson) !== "—" ? addressLine(F.ProviderAddressJson) : ""].filter(Boolean).join(" · "))}
              </div>
              <div>
                {row("Insurer", F.Insurer)}
                {row("Policy number", F.InsurancePolicyNumber)}
                {row("Policy holder", F.InsurancePolicyHolder)}
                {row("Group number", F.InsuranceGroupNumber)}
                {row("Medications", F.MedicationsText)}
                {row("Present illnesses", F.PresentIllnesses)}
                {row("Past illnesses", F.PastIllnesses)}
                {row("Physical limitations", F.PhysicalLimitations === "Y" ? `Yes${F.PhysicalLimitationsDetail ? `: ${F.PhysicalLimitationsDetail}` : ""}` : F.PhysicalLimitations === "N" ? "No" : "")}
              </div>
            </div>
            {d.can.edit && <button type="button" className="btn-secondary mt-3 min-h-11" onClick={() => setEditing(true)}>Edit the staff record</button>}
          </>
        ) : (
          <div className="mt-3">
            <StaffEditForm s={s} roles={d.roles} onCancel={() => setEditing(false)} onSaved={(staff, edits) => { setD({ ...d, staff, edits }); setEditing(false); setFlash("Saved"); }} />
          </div>
        )}
      </section>
      <StaffDocuments staffKey={s.staffKey} docs={d.documents} canAdd={d.can.edit} onDocs={(documents) => setD({ ...d, documents })} />
      <section className="section-card">
        <h3 className="mb-2 text-base font-semibold">Changes made in the app ({d.edits.length})</h3>
        {d.edits.length === 0 ? <p className="text-sm text-slate-600">None: the record is as {s.manual ? "added" : "imported"}.</p> : (
          <ol className="space-y-1 text-sm">
            {[...d.edits].reverse().map((e) => (
              <li key={e.noteKey}><span className="text-slate-500">{stamp(e.atUtc)} · {e.byName || e.byEmail}:</span> {Object.entries(e.changes).map(([k, c]) => `${d.labels[k] ?? k}: “${c.old || "—"}” → “${c.new || "—"}”`).join("; ")}</li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

export function Staff({ path }: { path: string }): React.ReactElement {
  const key = path.startsWith("/staff/") ? decodeURIComponent(path.slice("/staff/".length)) : null;
  return key ? <StaffRecord key={key} staffKey={key} /> : <StaffList />;
}
